import React from 'react'
import styled from '@emotion/styled'
import { UserPreference } from 'api'
import { BannerTileContainer, TileTitle, TileBody } from './heroPanelStyles'
import { CashAmount, getDirectionText } from './utils'
import { Decimal } from 'utils'
import { formatNumber } from 'locale/Locale'
import { Locale, Translation } from 'locale'
import * as constants from 'view/styles/constants'
import { getNotionalFormatOptions } from '../datagrid'
import { isNil } from 'lodash-es'

interface BannerCurrencyTileProps {
  cashAmount: CashAmount
  notional?: number
  cashLabel?: string
  gross?: boolean
  userPreference: UserPreference
}

const CurrencyTileTitle = styled(TileTitle)<{ notional?: number }>`
  font-size: ${props => (isNil(props.notional) ? undefined : '14px')};
  color: ${props => (isNil(props.notional) ? undefined : props.notional > 0 ? constants.DIRECTION_BUY : constants.DIRECTION_SELL)};
`

const NetCashAmount = styled.div<{ negative: boolean }>`
  font-family: NEXWeb;
  font-size: 18px;
  font-weight: ${constants.FontWeight.Medium};
  color: ${props => (props.negative ? constants.DIRECTION_SELL : constants.DIRECTION_BUY)};
  text-transform: uppercase;
  white-space: nowrap;
  margin-bottom: 4px;
`

const CurrencyRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 3px;
  &:last-of-type {
    margin-bottom: 0;
  }
`

const CashDirection = styled.span<{ labelWidth: number }>`
  width: ${props => props.labelWidth}px;
  margin-right: 5px;
  font-family: ProximaNova;
  font-size: 12px;
  line-height: 17px;
  color: ${constants.CORE_PRIMARY};
  opacity: 0.75;
  text-transform: uppercase;
`

const DirectionCount = styled.span`
  font-size: 12px;
  font-family: ProximaNova;
  font-weight: ${constants.FontWeight.SemiBold};
  color: ${constants.CORE_PRIMARY};
  opacity: 0.75;
  margin-right: 17px;
`

const DirectionAmount = styled.span`
  margin-left: auto;
  font-family: NEXWeb;
  font-size: 16px;
  font-weight: ${constants.FontWeight.Medium};
  color: ${constants.CORE_PRIMARY};
`

export const BannerCurrencyTile: React.FunctionComponent<BannerCurrencyTileProps> = props => {
  const { cashAmount, notional, cashLabel, gross, userPreference } = props
  const { currency, buyCount, buyAmount, sellCount, sellAmount } = cashAmount
  const amount = gross ? Decimal.add(buyAmount, sellAmount) : Decimal.subtract(buyAmount, sellAmount)
  const [buyDirection, sellDirection, directionLabelWidth] = getDirectionText(userPreference.directionFormat)
  const formattedNotional = notional
    ? formatNumber(notional, { ...getNotionalFormatOptions(userPreference.amountFormat, true), parenOnNegative: true })
    : null

  return (
    <BannerTileContainer>
      <CurrencyTileTitle notional={notional}>
        {formattedNotional ? `${formattedNotional} ${Locale.translate(Translation.TC_HERO_NOTIONAL)}` : currency}
      </CurrencyTileTitle>
      <TileBody>
        <NetCashAmount negative={amount < 0}>
          {formatNumber(amount, { minDp: 2, parenOnNegative: true })} {cashLabel || Locale.translate(Translation.TC_NET_CASH)}
        </NetCashAmount>
        <CurrencyRow>
          <CashDirection labelWidth={directionLabelWidth}>{buyDirection}</CashDirection>
          <DirectionCount>{buyCount}</DirectionCount>
          <DirectionAmount>{formatNumber(buyAmount, { minDp: 2 })}</DirectionAmount>
        </CurrencyRow>
        <CurrencyRow>
          <CashDirection labelWidth={directionLabelWidth}>{sellDirection}</CashDirection>
          <DirectionCount>{sellCount}</DirectionCount>
          <DirectionAmount>{formatNumber(sellAmount, { minDp: 2 })}</DirectionAmount>
        </CurrencyRow>
      </TileBody>
    </BannerTileContainer>
  )
}
